<template>

  <v-container fluid class="px-0 order-info-messages">

    <!--------------------------------------------->
    <!-- Messages to Driver (OrderDriverMessage) -->
    <!--------------------------------------------->
    <v-container fluid class="pl-8 page-content">
      <v-row>
        <v-col class="pl-3">
          <div class="title">
            <!-- <v-icon style="font-size:18px;" class="pr-2">mdi-message-text-outline</v-icon> -->
            Message for Driver
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="grey lighten-1"
                    style="font-size:20px;cursor:pointer;margin-left:8px"
                    v-bind="attrs"
                    v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <div>Add a message for the driver to view during delivery.</div>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>

      <order-driver-message-manager
          :order="order"
          :route="route" />
    </v-container>

    <!-------------------------------------------------->
    <!-- Messages from Receiver (OrderShipperMessage) -->
    <!-------------------------------------------------->
    <v-container fluid class="pl-10 pb-8 mt-5 page-content">
      <v-row>
        <v-col class="pl-0">
          <div class="title">
            <!-- <v-icon style="font-size:18px;" class="pr-2">mdi-message-text-outline</v-icon> -->
            Messages from {{ receiverLocationName }}
          </div>
        </v-col>
      </v-row>

      <v-container fluid class="pl-0" v-if="receiverMessageItems.length > 0">
        <!-- Message List Header -->
        <v-row class="mt-4 pb-1 message-list-header">
          <v-col cols="12" xs="12" sm="4" md="3" lg="3">
            Sent
          </v-col>
          <v-col cols="12" xs="12" sm="5" md="5" lg="6">
            Message
          </v-col>
          <v-col cols="12" xs="12" sm="3" md="4" lg="3" class="d-flex align-center justify-center">
            <span>Confirm as seen</span>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="grey lighten-1"
                    style="font-size:20px;cursor:pointer;margin-left:8px"
                    v-bind="attrs"
                    v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <div>Let {{ receiverLocationName }} know you have seen this message.</div>
            </v-tooltip>
          </v-col>
        </v-row>

        <!-- Message List Content -->
        <v-row class="mb-6 message-item-container" :class="{ 'not-seen': !item.is_confirmed }" v-for="(item, index) in receiverMessageItems" :key="`receiver-message-${index}`">
          <v-col cols="12" xs="12" sm="4" md="3" lg="3" class="d-flex align-center">
            <div :class="item.is_confirmed ? 'sent-date-seen' : 'sent-date'">{{ formatDateTime(item.created_at) }}</div>
          </v-col>
          <v-col cols="12" xs="12" sm="5" md="5" lg="6" class="d-flex align-center">
            <div :class="item.is_confirmed ? 'message-text-seen' : 'message-text'">{{ item.message }}</div>
          </v-col>
          <v-col cols="12" xs="12" sm="3" md="4" lg="3" class="d-flex align-center justify-center">
            <div class="confirmed-date" v-if="item.is_confirmed">
              {{ formatDateTime(item.confirmed_at) }}
            </div>
            <template v-else>
              <v-progress-circular indeterminate :size="20" color="grey" v-if="confirmingMessage === item.id" />
              <v-btn outlined height="30" color="amber darken-3" @click="confirmMessage(item)" v-else>
                <span style="font-size:0.84rem;">Confirm</span>
              </v-btn>
            </template>
          </v-col>
        </v-row>
      </v-container>

      <v-container fluid class="mt-5" v-else>
        <v-row>
          <v-col class="no-messages-text">
            (No messages)
          </v-col>
        </v-row>
      </v-container>

    </v-container>

  </v-container>

</template>

<script>
import OrderDriverMessageManager from '@/components/orderMessages/OrderDriverMessageManager'

export default {
  name: 'OrderInfoMessages',

  components: {
    OrderDriverMessageManager
  },

  props: {
    orderId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null,
      receiverMessageData: null,
      messageDataToView: null,
      confirmingMessage: null,
      confirmMessageError: null
    }
  },

  computed: {
    order () {
      return this.$parent.order
    },
    route () {
      return this.$parent.route
    },
    shipperLocationName () {
      return this.order?.shipper?.name || 'your org'
    },
    receiverLocationName () {
      return this.order?.receiver?.name || 'Receiver'
    },
    receiverMessageItems () {
      return (this.receiverMessageData) ? this.receiverMessageData.items : []
    }
  },

  async beforeMount () {
    await this.loadMessages()
  },

  methods: {
    // Display a date and time
    formatDateTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMM D, YYYY - h:mma') : ''
    },

    getMessagePreview (messageText) {
      const preview = (text, maxChars) => {
        if (text.length <= maxChars) return text

        // Find the last space within maxChars limit
        const lastSpace = text.substring(0, maxChars).lastIndexOf(' ')

        // If no space found, just cut at maxChars
        if (lastSpace === -1) return text.substring(0, maxChars) + '...'

        // Cut at the last space and add ellipsis
        return text.substring(0, lastSpace) + '...'
      }

      return preview(messageText, 80) // Adjust maxChars value as needed
    },

    // Confirms receipt of message
    async confirmMessage (item) {
      if (!item || !item.id) return false
      this.confirmingMessage = item.id

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `/order-shipper-message/${item.id}/confirm`
        })

        // Reload data
        this.loadMessages()
      } catch (err) {
        this.confirmMessageError = err.message || err.text || err
      } finally {
        this.confirmingMessage = null
      }
    },

    async loadMessages () {
      this.isDataLoading = true

      try {
        this.receiverMessageData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${this.orderId}/shipper-messages`,
          opts: {
            params: {
            }
          }
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style scoped>
  .order-info-messages .no-messages-text {
    color: #9c9c9c;
  }

  .order-info-messages .message-list-header {
    color: #9c9c9c;
    max-width: 900px;
  }

  .order-info-messages .message-item-container {
    border: 1px solid #525252;
    border-radius: 4px;
    max-width: 900px;
  }

  .order-info-messages .message-item-container.not-seen {
    border: 1px solid #a2a2a2;
  }

  .order-info-messages .sent-date {
    color: #b2b2b2;
    font-size: 0.9rem;
  }

  .order-info-messages .sent-date-seen {
    color: #9c9c9c;
    font-size: 0.9rem;
  }

  .order-info-messages .message-text-seen {
    color: #9c9c9c;
  }

  .order-info-messages .confirmed-date {
    color: #727272;
    font-size: 0.9rem;
  }

  a.item-link {
    text-decoration: none;
  }

  a.item-link:hover {
    text-decoration: underline;
  }
</style>
